// Browser-side FCM (Firebase Cloud Messaging) integration.
// Infrastructure layer — wraps the firebase/messaging SDK. Feature code talks to this
// via the notification feature's hook/usecases, not directly.
import type { MessagePayload, Messaging } from 'firebase/messaging';
import { FIREBASE_VAPID_KEY, firebaseConfig } from './firebaseConfig.js';

let messagingPromise: Promise<Messaging | null> | null = null;

/** Resolve a Messaging instance, or null when the browser can't do web push (Safari/iOS, no SW, etc.). */
function getMessagingIfSupported(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      try {
        const [appMod, msgMod] = await Promise.all([
          import('firebase/app'),
          import('firebase/messaging'),
        ]);
        const ok = await msgMod.isSupported().catch(() => false);
        if (!ok) return null;
        const app = appMod.getApps().length
          ? appMod.getApp()
          : appMod.initializeApp(firebaseConfig);
        return msgMod.getMessaging(app);
      } catch {
        return null;
      }
    })();
  }
  return messagingPromise;
}

export function isWebPushSupported(): Promise<boolean> {
  return getMessagingIfSupported().then((m) => m !== null);
}

/**
 * Ask for notification permission (if not decided), register the FCM service worker, and
 * mint a web push token for this browser. Returns null if unsupported / denied / not configured.
 */
export async function requestAndGetWebPushToken(): Promise<string | null> {
  const messaging = await getMessagingIfSupported();
  if (!messaging) {
    console.warn(
      '[webPush] Web push not supported in this context. Needs a SECURE context ' +
        '(HTTPS or http://localhost) with Service Worker + Push API. ' +
        'Current origin: ' + window.location.origin,
    );
    return null;
  }
  if (typeof Notification === 'undefined') return null;
  if (!FIREBASE_VAPID_KEY) {
    console.warn('[webPush] Missing VITE_FIREBASE_VAPID_KEY — skipping token registration.');
    return null;
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    console.warn('[webPush] Notification permission not granted:', permission);
    return null;
  }

  try {
    const { getToken } = await import('firebase/messaging');
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      console.info('[webPush] FCM token obtained:', token.slice(0, 12) + '…');
    } else {
      console.warn('[webPush] getToken returned empty (no token).');
    }
    return token || null;
  } catch (err) {
    console.error('[webPush] getToken failed:', err);
    return null;
  }
}

/** Foreground message handler (tab open/focused). Returns an unsubscribe fn. */
export async function onForegroundMessage(cb: (payload: MessagePayload) => void): Promise<() => void> {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  const { onMessage } = await import('firebase/messaging');
  return onMessage(messaging, cb);
}

/** Revoke this browser's token (call on logout before deleting the DB row). */
export async function deleteWebPushToken(): Promise<void> {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return;
  try {
    const { deleteToken } = await import('firebase/messaging');
    await deleteToken(messaging);
  } catch {
    /* token may already be gone — ignore */
  }
}
